/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { AccessForSelectFragment, FormItemAssigneesDocument } from 'generated/resources/graphql'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import LabeledInput from 'modules/core/components/LabeledInput'
import Select from 'modules/core/components/Select'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import Loading from 'modules/layout/components/Page/Loading'
import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { useValidationsProvider } from 'modules/processes/components/ItemWizard/Providers/validations'
import { ProcessCategories } from 'modules/processes/domain'
import {
  AssigneeStrategy,
  AssigneeTypeSubOptions,
  assigneeSelectionTypes,
} from 'modules/processes/lib/assigneeTypeSubOptions'

import TeamMultiSelectOption from './TeamMultiSelectOption'
import {
  assigneeData,
  initialAccessIdsResolver,
  initialAssigneeStrategyResolver,
  initialLocationIdResolver,
  initialTeamIdsResolver,
  initialTeamLeadTeamsIdsResolver,
} from './assigneeData'

interface Props {
  label?: string
  readonly?: boolean
  excludeAffectedUser?: boolean
  onlyForAffectedUser?: boolean
}

const Assignees = ({ readonly, label, onlyForAffectedUser, excludeAffectedUser }: Props) => {
  const { originalItem: item, form, changeValue, setInitialFormValue } = useFormProvider()

  const { setFieldValidation, removeFieldValidation } = useValidationsProvider()
  const strategy = form.get('assignee_strategy').value || initialAssigneeStrategyResolver(item)

  const teamIds = initialTeamIdsResolver(form, strategy, item)
  const locationId = initialLocationIdResolver(form, strategy, item)
  const teamLeadTeamsIds = initialTeamLeadTeamsIdsResolver(form, strategy, item)

  const assigneeStrategy = () => {
    if (strategy) return strategy
    if (onlyForAffectedUser) return AssigneeStrategy.AFFECTED_USER
    if (excludeAffectedUser) return AssigneeStrategy.MANAGER

    return AssigneeStrategy.AFFECTED_USER
  }

  const [initialAccessIds, setInitialAccessIds] = React.useState<string[]>([])

  useEffect(() => {
    const resolvedAccessIds = initialAccessIdsResolver(form, assigneeStrategy(), item)
    if (resolvedAccessIds && resolvedAccessIds.length) {
      setInitialAccessIds(resolvedAccessIds)
    }
  }, [form, item, onlyForAffectedUser, excludeAffectedUser])

  const { data, loading } = useQuery(FormItemAssigneesDocument, {
    variables: { ids: initialAccessIds },
    skip: !initialAccessIds?.length,
  })

  const [selectedAccesses, setSelectedAccesses] = React.useState<AccessForSelectFragment[]>([])

  useEffect(() => {
    if (data && data.apiCore?.accesses) {
      setSelectedAccesses(data.apiCore.accesses)
    }
  }, [data])

  const [accessIds, setAccessIds] = React.useState<string[]>(initialAccessIds)

  const { assignee_strategy, team_ids, location_id, team_lead_team_ids, access_ids } = form.values

  const { assigneeIds: newAssigneeIds } = assigneeData(form, accessIds)

  const { category } = useParams()

  const setAccessesAssignees = useCallback(
    (ids: string[]) => {
      setAccessIds(ids)
      form.setValues({ access_ids: ids })
    },
    [form]
  )

  useEffect(() => {
    if (onlyForAffectedUser) {
      form.setValues({
        assignee_strategy: AssigneeStrategy.AFFECTED_USER,
        access_ids: [],
        assigneeIds: [],
      })
    }

    setInitialFormValue({
      assignee_strategy: assigneeStrategy(),
      team_lead_team_ids: teamLeadTeamsIds,
      location_id: locationId,
      team_ids: teamIds,
      access_ids: accessIds,
    })

    if (access_ids) {
      setAccessesAssignees(newAssigneeIds)
    }
  }, [])

  useEffect(() => {
    if (assignee_strategy === AssigneeStrategy.MANUAL_SELECTION) {
      setFieldValidation({
        field: 'assignee_ids',
        validation: () => !!newAssigneeIds.length,
      })

      return () => {
        removeFieldValidation('assignee_ids')
      }
    }
  }, [newAssigneeIds])

  useEffect(() => {
    if (!selectedAccesses.length) return setAccessesAssignees([])

    setAccessesAssignees(selectedAccesses.map(({ id }) => id))
  }, [selectedAccesses, setAccessesAssignees])

  useEffect(() => {
    form.setValues({ assignee_ids: newAssigneeIds })
  }, [assignee_strategy, team_ids, location_id, team_lead_team_ids, access_ids])

  const assigneeTypes = assigneeSelectionTypes(
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    (category as ProcessCategories) || 'custom'
  )

  const allowedTypes = excludeAffectedUser
    ? assigneeTypes.filter(({ value }) => value !== AssigneeStrategy.AFFECTED_USER)
    : assigneeTypes

  if (loading) return <Loading />

  return (
    <Box gap="s24">
      <LabeledInput
        isLegend
        label={label || i18n.t('processes.process_settings.wizard.assignee.label')}
      >
        <Box gap="s6">
          <Select
            mandatory
            attachmentPosition="middle right"
            readonly={readonly}
            value={assignee_strategy}
            onChange={(value) => {
              changeValue('assignee_strategy', value)
            }}
            options={allowedTypes}
          />
          {assignee_strategy === AssigneeStrategy.TEAMS && (
            <TeamMultiSelectOption
              teamIds={team_ids || []}
              readonly={!!readonly}
              strategy={AssigneeStrategy.TEAMS}
              changeValue={changeValue}
              formKey="team_ids"
            />
          )}
          {assignee_strategy === AssigneeStrategy.TEAM_LEADS && (
            <TeamMultiSelectOption
              teamIds={team_lead_team_ids || []}
              readonly={!!readonly}
              strategy={AssigneeStrategy.TEAM_LEADS}
              changeValue={changeValue}
              formKey="team_lead_team_ids"
            />
          )}
          {assignee_strategy === AssigneeStrategy.WORKSPACE && (
            <Select
              mandatory
              attachmentPosition="middle right"
              readonly={readonly}
              value={String(location_id)}
              onChange={(value) => {
                changeValue('location_id', value)
              }}
              options={AssigneeTypeSubOptions[AssigneeStrategy.WORKSPACE]()}
            />
          )}
        </Box>
        {assignee_strategy === AssigneeStrategy.MANUAL_SELECTION && (
          <Box paddingTop="s12">
            <AccessSelector
              width="s400"
              multiple
              mandatory={selectedAccesses.length === 0}
              disabled={readonly}
              value={selectedAccesses}
              onSelect={(value: AccessForSelectFragment[]) => {
                setSelectedAccesses(value)
              }}
            />
          </Box>
        )}
      </LabeledInput>
    </Box>
  )
}

export default Assignees
